import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { accountAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, Wallet, History, User, LogOut, Menu, X, Home,
  Shield, CheckCircle, Clock, AlertCircle, FileText, Upload, Image, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { logout } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
    { path: '/account', label: 'Account', icon: <User className="w-5 h-5" /> },
  ];

  return (
    <>
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setOpen(false)} 
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 
        bg-[#0f0f11] border-r border-zinc-800 
        transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 flex flex-col
      `}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <button
            onClick={logout}
            className="nav-item w-full text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

// KYC Document Upload Card
function KYCDocumentUpload({ title, description, documentType, uploadedDoc, onUpload, uploading }) {
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Only JPEG, PNG, and PDF files are allowed');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    await onUpload(documentType, file);
    setFile(null);
  };

  const isUploaded = uploadedDoc?.filename;
  const isPending = uploadedDoc?.status === 'pending';
  const isVerified = uploadedDoc?.status === 'verified';

  return (
    <div className={`p-4 rounded-lg border ${
      isVerified ? 'border-green-500/30 bg-green-500/5' :
      isUploaded ? 'border-yellow-500/30 bg-yellow-500/5' :
      'border-zinc-700 bg-zinc-900/30'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {isVerified ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : isUploaded ? (
            <Clock className="w-5 h-5 text-yellow-400" />
          ) : (
            <FileText className="w-5 h-5 text-zinc-400" />
          )}
          <div>
            <h4 className="font-medium text-white">{title}</h4>
            <p className="text-xs text-zinc-500">{description}</p>
          </div>
        </div>
        {isVerified && (
          <span className="badge badge-success text-xs">Verified</span>
        )}
        {isPending && (
          <span className="badge badge-warning text-xs">Pending</span>
        )}
      </div>

      {!isUploaded ? (
        <div className="space-y-3">
          <label className="block cursor-pointer">
            <div className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
              file 
                ? 'border-primary/50 bg-primary/10' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}>
              <div className="flex items-center justify-center gap-2">
                {file ? (
                  <>
                    <Image className="w-5 h-5 text-primary" />
                    <span className="text-primary text-sm truncate max-w-[200px]">{file.name}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-zinc-400" />
                    <span className="text-zinc-400 text-sm">Click to select file</span>
                  </>
                )}
              </div>
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
          {file && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full"
              size="sm"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          )}
        </div>
      ) : (
        <div className="text-sm text-zinc-400">
          <p>Uploaded: {uploadedDoc.filename}</p>
          <p className="text-xs">{new Date(uploadedDoc.uploaded_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, updateUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const res = await accountAPI.getKYCStatus();
      setKycStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    setUploading(true);
    try {
      await accountAPI.uploadKYCDocument(documentType, file);
      toast.success(`${documentType.replace('_', ' ')} uploaded successfully!`);
      fetchKYCStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getKYCStatusIcon = () => {
    switch (user?.kyc_status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'rejected':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-zinc-400" />;
    }
  };

  const getKYCStatusBadge = () => {
    switch (user?.kyc_status) {
      case 'verified':
        return 'badge-success';
      case 'submitted':
        return 'badge-warning';
      case 'rejected':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  const kycDocuments = kycStatus?.kyc_documents || {};

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="account-page">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="flex-1 min-w-0">
        <header className="h-14 border-b border-zinc-800 flex items-center px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-zinc-400 hover:text-white mr-4"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold text-white font-['Outfit']">Account Settings</h1>
        </header>

        <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
          {/* Profile Card */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xl font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white" data-testid="user-name">
                  {user?.first_name} {user?.last_name}
                </h2>
                <p className="text-zinc-400" data-testid="user-email">{user?.email}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-zinc-400 text-sm">Account ID</Label>
                <p className="font-mono text-white text-lg mt-1">{user?.id}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Member Since</Label>
                <p className="text-white mt-1">{formatDate(user?.created_at)}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Account Type</Label>
                <p className="text-white mt-1 capitalize">{user?.account_type}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">KYC Status</Label>
                <div className="flex items-center gap-2 mt-1">
                  {getKYCStatusIcon()}
                  <span className={`badge ${getKYCStatusBadge()}`} data-testid="kyc-status">
                    {user?.kyc_status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Verification */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Identity Verification (KYC)</h2>
                <p className="text-sm text-zinc-400">Upload your documents to verify your identity</p>
              </div>
            </div>

            {user?.kyc_status === 'verified' ? (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="font-medium text-green-400">Verification Complete</p>
                  <p className="text-sm text-zinc-400">Your identity has been verified. You can now trade live.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {user?.kyc_status === 'submitted' && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mb-4">
                    <Clock className="w-6 h-6 text-yellow-400" />
                    <div>
                      <p className="font-medium text-yellow-400">Documents Under Review</p>
                      <p className="text-sm text-zinc-400">Your documents are being reviewed. This usually takes 1-2 business days.</p>
                    </div>
                  </div>
                )}

                {user?.kyc_status === 'rejected' && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
                    <XCircle className="w-6 h-6 text-red-400" />
                    <div>
                      <p className="font-medium text-red-400">Verification Rejected</p>
                      <p className="text-sm text-zinc-400">Your documents were rejected. Please re-upload clear, valid documents below.</p>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <KYCDocumentUpload
                    title="ID Front"
                    description="Front side of your ID/Passport"
                    documentType="id_front"
                    uploadedDoc={user?.kyc_status === 'rejected' ? null : kycDocuments.id_front}
                    onUpload={handleDocumentUpload}
                    uploading={uploading}
                  />
                  <KYCDocumentUpload
                    title="ID Back"
                    description="Back side of your ID"
                    documentType="id_back"
                    uploadedDoc={user?.kyc_status === 'rejected' ? null : kycDocuments.id_back}
                    onUpload={handleDocumentUpload}
                    uploading={uploading}
                  />
                  <KYCDocumentUpload
                    title="Selfie with ID"
                    description="Photo of yourself holding your ID"
                    documentType="selfie"
                    uploadedDoc={user?.kyc_status === 'rejected' ? null : kycDocuments.selfie}
                    onUpload={handleDocumentUpload}
                    uploading={uploading}
                  />
                  <KYCDocumentUpload
                    title="Proof of Address"
                    description="Utility bill or bank statement (last 3 months)"
                    documentType="address_proof"
                    uploadedDoc={user?.kyc_status === 'rejected' ? null : kycDocuments.address_proof}
                    onUpload={handleDocumentUpload}
                    uploading={uploading}
                  />
                </div>

                <p className="text-xs text-zinc-500 mt-4">
                  * Accepted formats: JPEG, PNG, PDF (max 5MB per file)
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
